import React, { JSX } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  ModelTraining,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import * as Types from 'types/types';
import {
  formatPayRange,
  formatJobDate,
  getOwnerName,
  jobSummaryText,
  SortField,
  SortOrder,
  truncateDescription,
} from './jobsViewUtils';
import { JobsViewTableHead } from './JobsViewTableHead';
import { JobsViewToolbar } from './JobsViewToolbar';

export interface JobsViewTableProps {
  jobs: Types.Job[];
  loading: boolean;
  total: number;
  page: number;
  limit: number;
  searchQuery: string;
  sortField: SortField;
  sortOrder: SortOrder;
  selectedJobs: Set<string>;
  selectedJob: Types.Job | null;
  selectedJobId?: string;
  selectable: boolean;
  showActions: boolean;
  isMobile: boolean;
  isSmall: boolean;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSort: (field: SortField) => void;
  onSortSelect: (field: SortField, order: SortOrder) => void;
  onSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectJob: (jobId: string) => void;
  onJobRowClick: (job: Types.Job) => void;
  onViewJob: (job: Types.Job) => void;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onJobEdit?: (job: Types.Job) => void;
  onJobRegenerate?: (job: Types.Job) => Promise<Types.Job>;
  onJobDelete?: (job: Types.Job) => Promise<void>;
  setJobs: React.Dispatch<React.SetStateAction<Types.Job[]>>;
  fetchJobs: (page?: number, search?: string) => Promise<void>;
}

export function JobsViewTable(props: JobsViewTableProps): JSX.Element {
  return (
    <>
      <JobsViewToolbar
        total={props.total}
        searchQuery={props.searchQuery}
        sortField={props.sortField}
        sortOrder={props.sortOrder}
        onSearchChange={props.onSearchChange}
        onSortSelect={props.onSortSelect}
      />
      <TableContainer sx={{ minWidth: 0, overflowX: 'auto' }}>
        <Table
          size="small"
          aria-label="jobs table"
          sx={{
            tableLayout: 'fixed',
            minWidth: props.isMobile ? 'auto' : '760px',
            '& .MuiTableCell-root': { px: 1 },
          }}
        >
          <JobsViewTableHead
            isMobile={props.isMobile}
            selectable={props.selectable}
            selectedCount={props.selectedJobs.size}
            rowCount={props.jobs.length}
            showActions={props.showActions}
            sortField={props.sortField}
            sortOrder={props.sortOrder}
            onSelectAll={props.onSelectAll}
            onSort={props.onSort}
          />
          <TableBody>
            {props.loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : props.jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No jobs found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              props.jobs.map((job, index) => (
                <JobRow key={job.id} job={job} index={index} {...props} />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={props.total}
        rowsPerPage={props.limit}
        page={props.page}
        onPageChange={props.onPageChange}
        onRowsPerPageChange={props.onRowsPerPageChange}
      />
    </>
  );
}

function JobRow(props: JobsViewTableProps & { job: Types.Job; index: number }): JSX.Element {
  const isItemSelected = props.selectedJobs.has(props.job.id || '');

  return (
    <TableRow
      hover
      selected={
        isItemSelected ||
        props.selectedJob?.id === props.job.id ||
        props.selectedJobId === props.job.id
      }
      onClick={() => props.onJobRowClick(props.job)}
      sx={{ cursor: 'pointer' }}
    >
      {props.selectable && (
        <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
          <Checkbox
            color="primary"
            checked={isItemSelected}
            onChange={() => props.onSelectJob(props.job.id || '')}
          />
        </TableCell>
      )}
      <TableCell>
        <Typography variant="body2" fontWeight="medium">
          {props.job.company}
        </Typography>
        {props.job.details?.location && (
          <Typography variant="caption" color="text.secondary">
            {props.job.details.location.city}, {props.job.details.location.state}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight="medium">
          {props.job.title}
        </Typography>
        {props.job.details?.employmentType && (
          <Chip
            label={props.job.details.employmentType}
            size="small"
            variant="outlined"
            sx={{ mt: 0.5 }}
          />
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="textSecondary">
          {truncateDescription(jobSummaryText(props.job), 100)}
        </Typography>
        {props.isMobile && <MobileJobMeta {...props} />}
      </TableCell>
      {!props.isMobile && <DesktopJobMeta {...props} />}
      {!props.isMobile && (
        <TableCell align="center" onClick={e => e.stopPropagation()}>
          <ViewButton job={props.job} onViewJob={props.onViewJob} />
        </TableCell>
      )}
      {!props.isMobile && props.showActions && (
        <TableCell align="center" onClick={e => e.stopPropagation()}>
          <JobActions {...props} />
        </TableCell>
      )}
    </TableRow>
  );
}

function MobileJobMeta(props: JobsViewTableProps & { job: Types.Job }): JSX.Element {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
      {(props.job.payMinimum || props.job.payMaximum) && (
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
          {formatPayRange(props.job.payMinimum, props.job.payMaximum)}
        </Typography>
      )}
      <ViewButton job={props.job} onViewJob={props.onViewJob} />
    </Box>
  );
}

function DesktopJobMeta(props: JobsViewTableProps & { job: Types.Job }): JSX.Element {
  return (
    <>
      <TableCell>
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {formatPayRange(props.job.payMinimum, props.job.payMaximum)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {getOwnerName(props.job.owner)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {formatJobDate(props.job.updatedAt, props.isMobile, props.isSmall)}
        </Typography>
      </TableCell>
    </>
  );
}

function JobActions(props: JobsViewTableProps & { job: Types.Job; index: number }): JSX.Element {
  return (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      {props.onJobEdit && (
        <Tooltip title="Edit Job">
          <IconButton size="small" onClick={() => props.onJobEdit?.(props.job)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {props.onJobRegenerate && (
        <Tooltip title="Regenerate Requirements">
          <IconButton
            size="small"
            onClick={async () => {
              const generatedJob = await props.onJobRegenerate?.(props.job);
              if (!generatedJob) return;
              props.jobs[props.index] = generatedJob;
              props.setJobs([...props.jobs]);
            }}
          >
            <ModelTraining fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {props.onJobDelete && (
        <Tooltip title="Delete Job">
          <IconButton
            size="small"
            onClick={async () => {
              await props.onJobDelete?.(props.job);
              props.fetchJobs(0, props.searchQuery);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function ViewButton(props: { job: Types.Job; onViewJob: (job: Types.Job) => void }): JSX.Element {
  return (
    <Tooltip title="View Details">
      <IconButton
        size="small"
        sx={{ ml: 'auto' }}
        onClick={e => {
          e.stopPropagation();
          props.onViewJob(props.job);
        }}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}
